import type { SxProps, Theme } from '@mui/material';
import { LINE_HEIGHT, SCAN_FRACTION } from './data';

// --- ScannerAnimation ---

export const pipelineOuterContainerSx: SxProps<Theme> = {
  width: '100%',
  bgcolor: '#f4f2ee',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: 3,
  py: 2.5,
  pb: 4,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 0,
    backgroundImage:
      'radial-gradient(circle, rgba(0,0,0,0.09) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
  },
};

export const pipelineHeaderSx: SxProps<Theme> = {
  textAlign: 'center',
  mb: 2.75,
  mt: 2,
  position: 'relative',
  zIndex: 1,
};

export const pipelineHeadingTypographySx: SxProps<Theme> = {
  fontWeight: 300,
  letterSpacing: '-0.02em',
  color: '#111418',
  lineHeight: 1.15,
};

export const pipelineHeadingStrongSx: SxProps<Theme> = {
  fontWeight: 600,
};

export const pipelineCardSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'stretch',
  justifySelf: 'center',
  textAlign: 'center',
  width: '100%',
  maxWidth: 1040,
  minHeight: 420,
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 4px 6px rgba(0,0,0,0.04), 0 20px 60px rgba(0,0,0,0.08)',
  position: 'relative',
  zIndex: 1,
};

export const pipelineUrlFeedColumnSx: SxProps<Theme> = {
  flex: {
    xs: '0 0 0%',
    md: '0 0 33.333%',
    lg: '0 0 25%',
  },
  display: {
    xs: 'none',
    md: 'flex',
  },
  flexDirection: 'column',
  p: '18px 16px',
  position: 'relative',
};

// --- ScannerColumn ---

export const scannerColumnOuterSx: SxProps<Theme> = {
  flex: {
    xs: '1 1 50%',
    md: '1 1 33.333%',
  },
  minWidth: 0,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  borderLeft: '1px solid rgba(0,0,0,0.08)',
  borderRight: '1px solid rgba(0,0,0,0.08)',
  position: 'relative',
};

export const scannerBrowserChromeSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: '14px',
  py: '10px',
  bgcolor: 'rgba(0,0,0,0.04)',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  flexShrink: 0,
};

export const scannerDotSx: SxProps<Theme> = {
  width: 8,
  height: 8,
  borderRadius: '50%',
};

export const scannerAddressBarSx: SxProps<Theme> = {
  flex: 1,
  textAlign: 'center',
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '9.5px',
  color: '#7a8290',
  bgcolor: 'rgba(0,0,0,0.04)',
  border: '1px solid rgba(0,0,0,0.06)',
  borderRadius: '4px',
  px: '10px',
  py: '3px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};

export const scannerSectionLabelRowSx: SxProps<Theme> = {
  px: '14px',
  py: '7px',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  flexShrink: 0,
};

export const scannerSectionLabelDotSx: SxProps<Theme> = {
  width: 5,
  height: 5,
  borderRadius: '50%',
  bgcolor: '#1a56ff',
};

export const scannerSectionLabelTypographySx: SxProps<Theme> = {
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#b0b6be',
};

export const scannerViewportSx: SxProps<Theme> = {
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
  bgcolor: '#f8f7f4',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: '44px',
    background: 'linear-gradient(to bottom, #f8f7f4, transparent)',
    zIndex: 5,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '44px',
    background: 'linear-gradient(to top, #f8f7f4, transparent)',
    zIndex: 5,
    pointerEvents: 'none',
  },
};

export const scannerBeamPositionerSx: SxProps<Theme> = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: `${SCAN_FRACTION * 100}%`,
  zIndex: 10,
  pointerEvents: 'none',
};

export const scannerLineNumberSx: SxProps<Theme> = {
  position: 'absolute',
  left: '6px',
  width: '26px',
  textAlign: 'right',
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '8.5px',
  color: 'rgba(0,0,0,0.12)',
  opacity: 0.5,
  userSelect: 'none',
};

export function getScannerBeamLineSx(beamFlash: boolean): SxProps<Theme> {
  return {
    height: '1.5px',
    background:
      'linear-gradient(90deg, transparent 0%, #1a56ff 15%, #1a56ff 85%, transparent 100%)',
    boxShadow: beamFlash
      ? '0 0 30px rgba(26,86,255,0.9), 0 0 70px rgba(26,86,255,0.5)'
      : '0 0 8px rgba(26,86,255,0.5), 0 0 22px rgba(26,86,255,0.25)',
    animation: 'ndBreathe 2s ease-in-out infinite',
    transition: 'box-shadow 0.1s',
  };
}

export function getScannerScrollingCodeContainerSx(
  scanOffset: number
): SxProps<Theme> {
  return {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    transform: `translateY(${scanOffset}px)`,
    willChange: 'transform',
  };
}

export function getScannerCodeLineSx(
  rowBackgroundColor: string,
  lineColor: string,
  isComment: boolean
): SxProps<Theme> {
  return {
    display: 'flex',
    alignItems: 'center',
    height: `${LINE_HEIGHT}px`,
    pl: '40px',
    pr: '10px',
    fontFamily: '"IBM Plex Mono", monospace',
    fontSize: '10px',
    whiteSpace: 'nowrap',
    position: 'relative',
    color: lineColor,
    fontStyle: isComment ? 'italic' : 'normal',
    bgcolor: rowBackgroundColor,
    transition: 'background 0.1s',
  };
}

// --- SharedComponents ---

export const columnLabelStackSx: SxProps<Theme> = {
  mb: 1.5,
  flexShrink: 0,
};

export const columnLabelDotSx: SxProps<Theme> = {
  width: 5,
  height: 5,
  borderRadius: '50%',
  bgcolor: '#1a56ff',
  flexShrink: 0,
};

export const columnLabelTypographySx: SxProps<Theme> = {
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#b0b6be',
};

// --- TechColumn ---

export const techColumnOuterSx: SxProps<Theme> = {
  flex: {
    xs: '1 1 50%',
    md: '1 1 33.333%',
    lg: '1 1 25%',
  },
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  p: '18px 16px',
};

export const techColumnListSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '7px',
  flex: 1,
};

export const techColumnDividerSx: SxProps<Theme> = {
  my: 1.25,
};

export const techColumnFooterRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const techColumnTechImageSx: SxProps<Theme> = {
  flexShrink: 0,
  borderRadius: '6px',
};

export const techColumnTechNameTypographySx: SxProps<Theme> = {
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '11px',
  fontWeight: 500,
  flex: 1,
  color: '#333',
};

export const techColumnTechsDetectedLabelSx: SxProps<Theme> = {
  fontSize: '10px',
  color: '#7a8290',
  mt: 0.25,
};

export const techColumnIndexedLabelSx: SxProps<Theme> = {
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '8.5px',
  color: '#b0b6be',
  textAlign: 'right',
  lineHeight: 1.7,
};

export function getTechBadgeSx(visible: boolean): SxProps<Theme> {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    px: '11px',
    py: '8px',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: visible ? 'rgba(26,86,255,0.2)' : 'transparent',
    bgcolor: visible ? 'rgba(26,86,255,0.05)' : 'transparent',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateX(0)' : 'translateX(10px)',
    transition:
      'opacity 0.38s cubic-bezier(0.34,1.56,0.64,1), transform 0.38s cubic-bezier(0.34,1.56,0.64,1), background 0.3s, border-color 0.3s',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'rgba(26,86,255,0.4)',
      bgcolor: 'rgba(26,86,255,0.1)',
    },
  };
}

export function getTechCheckmarkSx(visible: boolean): SxProps<Theme> {
  return {
    fontSize: '10px',
    color: '#1a56ff',
    flexShrink: 0,
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.3s',
  };
}

export const techColumnCountSx: SxProps<Theme> = {
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '22px',
  fontWeight: 600,
  color: '#1a56ff',
  lineHeight: 1,
};

// --- UrlFeed ---

export const urlFeedContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  flex: 1,
};

export const urlRowSpinnerSx: SxProps<Theme> = {
  width: 9,
  height: 9,
  borderRadius: '50%',
  flexShrink: 0,
  border: '1.5px solid rgba(26,86,255,0.15)',
  borderTopColor: '#1a56ff',
  animation: 'ndSpin 0.7s linear infinite',
  zIndex: 1,
};

export const urlRowDoneCheckmarkSx: SxProps<Theme> = {
  fontSize: '9px',
  color: '#1a56ff',
  ml: 'auto',
  zIndex: 1,
};

export function getUrlRowContainerSx(isActive: boolean): SxProps<Theme> {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    px: '9px',
    py: '6px',
    borderRadius: '7px',
    border: '1px solid',
    borderColor: isActive ? 'rgba(26,86,255,0.2)' : 'transparent',
    bgcolor: isActive ? 'rgba(26,86,255,0.08)' : '#f8f7f4',
    position: 'relative',
    overflow: 'hidden',
    transition: 'background 0.25s, border-color 0.25s',
    flexShrink: 0,
  };
}

export function getUrlRowProgressFillSx(isActive: boolean): SxProps<Theme> {
  return {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    bgcolor: 'rgba(26,86,255,0.05)',
    borderRadius: '7px',
    width: isActive ? '100%' : '0%',
    transition: isActive ? 'width 2.1s linear' : 'none',
  };
}

export function getUrlRowFaviconLetterSx(faviconColor: string): SxProps<Theme> {
  return {
    width: 14,
    height: 14,
    borderRadius: '3px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: faviconColor,
    fontFamily: '"IBM Plex Mono", monospace',
    fontSize: '6.5px',
    fontWeight: 700,
    color: '#fff',
    zIndex: 1,
  };
}

export function getUrlRowHostTypographySx(textColor: string): SxProps<Theme> {
  return {
    fontFamily: '"IBM Plex Mono", monospace',
    fontSize: '9.5px',
    color: textColor,
    flex: 1,
    zIndex: 1,
    transition: 'color 0.25s',
    maxWidth: 130,
  };
}
